<template>
	<div v-if="loading" class="loading-container">
		<i class="fas fa-spinner loading-spinner" />
	</div>
	<Sizer
		submitPath="/sizer/sizeWithUtilityCalc/"
		:initFormData="initFormValues"
		:onResults="onResults"
		submitText="Calculate Utility Costs"
		:submitMethod="handleUtilitySubmit"
	>
		<template v-slot:banner>
			<UtilityCalcIntro />
		</template>

		<template v-slot:results>
			<UtilityCalcResults />
		</template>

		<template v-slot:form-sections>
			<BuildingTypeSelect />

			<CASpecToggle v-if="formData.buildingType === 'multi_family'" />

			<MultifamilyOccupancyMethodToggle
				v-if="formData.buildingType === 'multi_family'"
			/>

			<OccupancyInputs />

			<WeatherStationSelect />

			<TempMaintSystSelect />

			<WaterTempInputs />

			<EquipmentSelection />

			<UtilityCalcInputs @file-selected="handleFileUpload" />

			<LoadShift :showPercentSlider="false" :showPeriods="true" />
		</template>
	</Sizer>
</template>

<script>
import { getCsrfToken } from '@/utils/json-fetch'
import Sizer from '@/components/Sizer'
// components
import UtilityCalcIntro from '@/components/UtilityCalcIntro'
import UtilityCalcResults from '@/components/UtilityCalcResults'
import BuildingTypeSelect from '@/components/form-sections/BuildingTypeSelect'
import WeatherStationSelect from '@/components/form-sections/WeatherStationSelect'
import CASpecToggle from '@/components/form-sections/CASpecToggle'
import MultifamilyOccupancyMethodToggle from '@/components/form-sections/MultifamilyOccupancyMethodToggle'
import OccupancyInputs from '@/components/form-sections/OccupancyInputs'
import WaterTempInputs from '@/components/form-sections/WaterTempInputs'
import UtilityCalcInputs, { emptyChargingPeriod } from '@/components/form-sections/UtilityCalcInputs'
import TempMaintSystSelect from '@/components/form-sections/TempMaintSystSelect'
import EquipmentSelection from '@/components/form-sections/EquipmentSelection'
import LoadShift from '@/components/form-sections/LoadShift'
// data stores
import formData, { defaultFormDataValues } from '@/components/Sizer/form-data-store'
import sizerStore from '@/components/Sizer/sizer-store'

export default {
	name: 'UtilityCalcSizer',

	components: {
		Sizer,
		UtilityCalcIntro,
		UtilityCalcResults,
		BuildingTypeSelect,
		WeatherStationSelect,
		CASpecToggle,
		MultifamilyOccupancyMethodToggle,
		OccupancyInputs,
		WaterTempInputs,
		TempMaintSystSelect,
		EquipmentSelection,
		UtilityCalcInputs,
		LoadShift,
	},

	data: () => ({
		formData,
		sizerStore,
		selectedFile: null,
		loading: false, 
		initFormValues: {
			...defaultFormDataValues,
			schematic: 'singlepass_norecirc',
			HPWHModelSP: null,
			numberPrimHPWH: null,
			primaryStorageVol: null,
			swingTankElement: 10,
			baseUtilityMonthlyCharges: 39.50,
			csv_file : null,
			chargingPeriods: [{ ...emptyChargingPeriod }],
			climateZone: null,
		},
	}),

	created () {
		if (!sizerStore.out) {
			this.$router.replace('/sizer-utility-calc/')
		}
	},

	methods: {
		handleFileUpload(file) {
			formData.csv_file = file; // Bind file to form values
		},
		async handleUtilitySubmit() {

			if (!formData.HPWHModelSP) {
				sizerStore.errors = { HPWHModelSP: ['Please select a Heat Pump Water Heater Model.'] }
				sizerStore.out = null
				this.onResults({ success: false })
				return
			}

			const { onResults, formatOutData } = this
			this.loading = true
			this.resultsData = null

			const csrfToken = getCsrfToken();

			if (!csrfToken) {
				console.error("CSRF token missing!");
				return;
			}

			let formDataToSubmit = new FormData();

			Object.entries(formData).forEach(([key, value]) => {
				if (key === "chargingPeriods") {
					// Serialize the object to JSON
					formDataToSubmit.append(key, JSON.stringify(value));
				}else if (value !== null && value !== 'undefined') {
					formDataToSubmit.append(key, value);
				}
			});

			try {
				let response = await fetch("/sizer/sizeWithUtilityCalc/", {
					method: "POST",
					body: formDataToSubmit,
					headers: {
						"X-CSRFToken": csrfToken,
					},
				});
				// let data = await response.json();
				const {
					errors,
					out,
					primaryCurveDiv,
					plotSimDiv,
				} = await response.json();
				sizerStore.out = out && formatOutData(out)
				sizerStore.primaryCurveDiv = primaryCurveDiv
				sizerStore.plotSimDiv = plotSimDiv
				sizerStore.errors = errors
				// formData.TMCap_kW = !formData.sizeAdditionalER && out && out.swingtankkW || formData.TMCap_kW
				this.loading = false
				onResults({ success: !errors })
			} catch (error) {
				console.error("Error submitting form:", error);
			}
		},
		formatOutData (out) {
			const [x_data, y_data] = [out.x_data, out.y_data].map(data => {
				return data && JSON.parse(data)
			})

			return {
				...out,
				x_data,
				y_data,
			}
		},
		onResults () {
			const { $router: { push } } = this

			push('/sizer-utility-calc/size')
		}
	}
}
</script>

<style scoped>
.loading-container {
    z-index: 10;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: black;
    opacity: 0.3;
}

.loading-spinner {
    font-size: 3rem;
    color: white;
    animation: spin 2s linear infinite;
    position: absolute;
    top: 45%;
    left: 45%;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
